import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/config";
import { AssessmentButton } from "@/components/Ctas";

export function useAnchorNav() {
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();
  return (hash) => {
    const scroll = () => {
      lenis?.scrollTo(hash, { offset: -72, duration: 1.1 });
      // Corrective pass once the animated scroll settles, so late font/image
      // layout shifts can't leave the wrong section in view.
      setTimeout(() => lenis?.scrollTo(hash, { offset: -72, duration: 0.5 }), 1300);
    };
    if (location.pathname !== "/") {
      navigate("/");
      // Wait until the target section actually exists before scrolling.
      let tries = 0;
      const poll = setInterval(() => {
        if (document.querySelector(hash) || ++tries > 40) {
          clearInterval(poll);
          scroll();
        }
      }, 50);
    } else {
      scroll();
    }
  };
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const go = useAnchorNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handle = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    go(hash);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
        scrolled
          ? "border-line bg-paper/90 backdrop-blur-md"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          data-testid="nav-logo"
          href="/"
          onClick={(e) => handle(e, "#top")}
          aria-label="KP Strategy — Product & Systems, back to top"
        >
          <img
            src="/assets/kp-logo.png"
            alt="KP Strategy — Product & Systems"
            className="h-10 w-auto sm:h-11"
          />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                data-testid={l.testid}
                to={l.to}
                className="text-[13px] font-medium tracking-wide text-body transition-colors duration-300 hover:text-amber"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                data-testid={l.testid}
                href={l.hash}
                onClick={(e) => handle(e, l.hash)}
                className="text-[13px] font-medium tracking-wide text-body transition-colors duration-300 hover:text-amber"
              >
                {l.label}
              </a>
            )
          )}
          <AssessmentButton testid="nav-cta-assessment" dark={false} className="!px-5 !py-2.5" />
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-ink lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line bg-paper lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((l) =>
                l.to ? (
                  <Link
                    key={l.label}
                    data-testid={`${l.testid}-mobile`}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="border-b border-line/60 py-3.5 font-serif text-2xl font-light text-ink transition-colors hover:text-amber"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    data-testid={`${l.testid}-mobile`}
                    href={l.hash}
                    onClick={(e) => handle(e, l.hash)}
                    className="border-b border-line/60 py-3.5 font-serif text-2xl font-light text-ink transition-colors hover:text-amber"
                  >
                    {l.label}
                  </a>
                )
              )}
              <div className="pt-5">
                <AssessmentButton testid="nav-cta-assessment-mobile" dark={false} />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
